import { useEffect, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar,
} from 'recharts';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import { FiCalendar, FiTrendingUp, FiCheckCircle, FiX, FiClock } from 'react-icons/fi';

const StatusBadge = ({ status }) => {
  const map   = { SUCCESS: 'badge-success', FAILED: 'badge-failed', PENDING: 'badge-pending', REFUNDED: 'badge-refunded' };
  const icons = { SUCCESS: '✓', FAILED: '✕', PENDING: '⏳', REFUNDED: '↩' };
  return <span className={`badge ${map[status] || 'badge-pending'}`}>{icons[status]} {status}</span>;
};

const PIE_COLORS   = { DIRECT: '#7c5cfc', LINK: '#f5a623', QR: '#00d68f' };
const EVENT_COLORS = ['#7c5cfc', '#00d68f', '#f5a623', '#29b6f6', '#ef5350', '#ab47bc', '#26a69a', '#ff7043', '#66bb6a', '#ec407a'];

export default function Dashboard() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const { data } = await API.get('/payment/stats');
      setStats(data);
    } catch {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
        <span className="spinner" style={{ borderTopColor: 'var(--primary)' }} />
        <span style={{ color: 'var(--text3)' }}>Loading dashboard...</span>
      </div>
    </Layout>
  );

  const s = stats?.stats || {};

  const chartData = (stats?.chartData || []).map(r => ({
    date:         new Date(r.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
    revenue:      parseFloat(r.revenue || 0),
    transactions: parseInt(r.transactions || 0),
  }));

  const pieData = (stats?.typeBreakdown || []).map(r => ({
    name: r.payment_type, value: parseFloat(r.total || 0),
  }));

  const eventData = (stats?.eventBreakdown || []).map(r => ({
    event:         r.event_name,
    revenue:       parseFloat(r.total_revenue || 0),
    success:       parseInt(r.success_count  || 0),
    failed:        parseInt(r.failed_count   || 0),
    pending:       parseInt(r.pending_count  || 0),
    total:         parseInt(r.total_txns     || 0),
  }));

  const topEvent = eventData.find(e => e.event !== 'Untagged') || eventData[0];

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Your payment overview for SteponePay</p>
      </div>

      <div className="page-body fade-in">
        {/* ── Stat Cards ── */}
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(0,214,143,0.12)', color: 'var(--success)' }}>₹</div>
            <div className="stat-label">Today's Revenue</div>
            <div className="stat-value" style={{ color: 'var(--success)' }}>₹{parseFloat(s.today?.total || 0).toLocaleString('en-IN')}</div>
            <div className="stat-sub">{s.today?.count || 0} transactions</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(124,92,252,0.12)', color: 'var(--primary)' }}>📅</div>
            <div className="stat-label">This Month</div>
            <div className="stat-value">₹{parseFloat(s.month?.total || 0).toLocaleString('en-IN')}</div>
            <div className="stat-sub">{s.month?.count || 0} successful</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(245,166,35,0.12)', color: 'var(--gold)' }}>🏆</div>
            <div className="stat-label">Total Revenue</div>
            <div className="stat-value" style={{ color: 'var(--gold)' }}>₹{parseFloat(s.total?.total || 0).toLocaleString('en-IN')}</div>
            <div className="stat-sub">{s.total?.count || 0} all time</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'rgba(41,182,246,0.12)', color: 'var(--pending)' }}>⏳</div>
            <div className="stat-label">Pending</div>
            <div className="stat-value" style={{ color: 'var(--pending)' }}>{s.pending || 0}</div>
            <div className="stat-sub">{s.failed || 0} failed</div>
          </div>
        </div>

        {/* ── Charts Row ── */}
        <div className="two-col" style={{ marginBottom: 24 }}>
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Revenue — Last 7 Days</h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#7c5cfc" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#7c5cfc" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#606080' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#606080' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, fontSize: 12 }}
                    formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#7c5cfc" fill="url(#rev)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state"><div className="empty-icon">📈</div><p>No data yet</p></div>
            )}
          </div>

          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Revenue by Payment Type</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[entry.name] || '#7c5cfc'} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={v => [`₹${parseFloat(v).toLocaleString('en-IN')}`, 'Revenue']}
                    contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend formatter={v => <span style={{ fontSize: 12, color: 'var(--text2)' }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state"><div className="empty-icon">🥧</div><p>No data yet</p></div>
            )}
          </div>
        </div>

        {/* ── EVENT ANALYTICS ── */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="flex items-center justify-between mb-16">
            <h3 style={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiCalendar style={{ color: 'var(--primary)' }} /> Event Analytics
            </h3>
            {topEvent && (
              <span style={{ fontSize: 12, color: 'var(--text3)', background: 'var(--bg3)', padding: '3px 10px', borderRadius: 20 }}>
                🏆 Top: <strong style={{ color: 'var(--text)' }}>{topEvent.event}</strong>
              </span>
            )}
          </div>

          {eventData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎫</div>
              <p>No event data yet. Tag payments with an event name when creating them.</p>
            </div>
          ) : (
            <>
              {/* Bar chart for event revenue */}
              {eventData.length > 1 && (
                <ResponsiveContainer width="100%" height={200} style={{ marginBottom: 20 }}>
                  <BarChart data={eventData} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
                    <XAxis
                      dataKey="event"
                      tick={{ fontSize: 11, fill: '#606080' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => v.length > 14 ? v.slice(0, 14) + '…' : v}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#606080' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => `₹${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`}
                    />
                    <Tooltip
                      contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, fontSize: 12 }}
                      formatter={v => [`₹${parseFloat(v).toLocaleString('en-IN')}`, 'Revenue']}
                    />
                    <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                      {eventData.map((_, i) => (
                        <Cell key={i} fill={EVENT_COLORS[i % EVENT_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Event table */}
              <div className="table-wrap" style={{ marginTop: 8 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Revenue</th>
                      <th>Total Txns</th>
                      <th style={{ color: 'var(--success)' }}>
                        <FiCheckCircle style={{ marginRight: 4 }} />Success
                      </th>
                      <th style={{ color: 'var(--error)' }}>
                        <FiX style={{ marginRight: 4 }} />Failed
                      </th>
                      <th style={{ color: 'var(--pending)' }}>
                        <FiClock style={{ marginRight: 4 }} />Pending
                      </th>
                      <th>Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventData.map((ev, i) => {
                      const convRate = ev.total > 0 ? Math.round((ev.success / ev.total) * 100) : 0;
                      return (
                        <tr key={ev.event}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                width: 10, height: 10, borderRadius: '50%',
                                background: EVENT_COLORS[i % EVENT_COLORS.length],
                                flexShrink: 0,
                              }} />
                              <span style={{ fontWeight: 500, color: ev.event === 'Untagged' ? 'var(--text3)' : 'var(--text)' }}>
                                {ev.event === 'Untagged' ? <em>Untagged</em> : ev.event}
                              </span>
                            </div>
                          </td>
                          <td style={{ fontWeight: 700, color: 'var(--success)' }}>
                            ₹{ev.revenue.toLocaleString('en-IN')}
                          </td>
                          <td style={{ color: 'var(--text2)' }}>{ev.total}</td>
                          <td>
                            <span style={{ color: 'var(--success)', fontWeight: 600 }}>{ev.success}</span>
                          </td>
                          <td>
                            <span style={{ color: ev.failed > 0 ? 'var(--error)' : 'var(--text3)' }}>{ev.failed}</span>
                          </td>
                          <td>
                            <span style={{ color: ev.pending > 0 ? 'var(--pending)' : 'var(--text3)' }}>{ev.pending}</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{
                                flex: 1, height: 6, background: 'var(--bg3)',
                                borderRadius: 3, overflow: 'hidden', minWidth: 60,
                              }}>
                                <div style={{
                                  height: '100%', borderRadius: 3,
                                  width: `${convRate}%`,
                                  background: convRate >= 70 ? 'var(--success)' : convRate >= 40 ? 'var(--gold)' : 'var(--error)',
                                }} />
                              </div>
                              <span style={{ fontSize: 12, color: 'var(--text2)', minWidth: 32 }}>{convRate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ── Recent Transactions ── */}
        <div className="card">
          <div className="flex items-center justify-between mb-16">
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Recent Transactions</h3>
            <button className="btn btn-outline btn-sm" onClick={() => window.location.href = '/transactions'}>
              View All →
            </button>
          </div>

          {(stats?.recent || []).length > 0 ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Customer</th>
                    <th>Event</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.recent || []).map(txn => (
                    <tr key={txn.id}>
                      <td className="font-mono">{txn.merchant_transaction_id}</td>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{txn.customer_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{txn.customer_phone}</div>
                      </td>
                      <td>
                        {txn.event_name
                          ? <span style={{ fontSize: 12, color: 'var(--primary)', background: 'rgba(124,92,252,0.1)', padding: '2px 8px', borderRadius: 4 }}>🎫 {txn.event_name}</span>
                          : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
                        }
                      </td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>₹{parseFloat(txn.amount).toLocaleString('en-IN')}</td>
                      <td>
                        <span style={{ fontSize: 12, color: 'var(--text3)', background: 'var(--bg3)', padding: '2px 8px', borderRadius: 4 }}>
                          {txn.payment_type}
                        </span>
                      </td>
                      <td><StatusBadge status={txn.status} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                        {new Date(txn.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td>
  {txn.created_by_username
    ? (
      <span style={{
        fontSize: 12,
        color: 'var(--text2)',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span style={{
          width: 20, height: 20, borderRadius: '50%',
          background: 'rgba(124,92,252,0.15)',
          color: 'var(--primary)',
          display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 10, fontWeight: 700,
          flexShrink: 0,
        }}>
          {(txn.created_by_name || txn.created_by_username)
            .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
        </span>
        {txn.created_by_username}
      </span>
    )
    : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
  }
</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>No transactions yet. Create your first payment!</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}